import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await apiService.getCurrentUser();
          setUser(userData);
        } catch (error) {
          console.error('Error al obtener usuario actual:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Efecto separado para la renovación automática de tokens
  useEffect(() => {
    if (!user) return; // Solo configurar intervalo si hay usuario

    console.log('Configurando renovación automática de tokens para usuario:', user.email);
    
    const tokenRefreshInterval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          console.log('Renovando token automáticamente...');
          await apiService.refreshToken();
        } catch (error) {
          console.error('Error en renovación automática de token:', error);
        }
      }
    }, 30 * 60 * 1000); // Renovar cada 30 minutos (menos frecuente)

    return () => {
      console.log('Limpiando intervalo de renovación de tokens');
      clearInterval(tokenRefreshInterval);
    };
  }, [user]);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login({ email, password });
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (error: any) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await apiService.register(userData);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    // Opcional: llamar al endpoint de logout del backend
    apiService.logout().catch(console.error);
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const updatedUser = await apiService.updateProfile(userData);
      console.log('🔍 Usuario actualizado desde API:', updatedUser);
      
      // Preservar el rol original si no viene en la respuesta
      const finalUser = {
        ...updatedUser,
        role: updatedUser.role || user?.role, // Preservar rol original si no viene
      };
      
      console.log('🔍 Usuario final con rol preservado:', finalUser);
      setUser(finalUser);
      localStorage.setItem('user', JSON.stringify(finalUser));
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 